import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import Modal from '../../Modal';
import ActionButton from '../../ActionButton';
import InputNew from '../../InputNew';
import DownScreenMessage from '../../DownScreenMessage';
import MultilineInput from '../components/MessageInput';
import { closeBatchMessageTemplate, setBatchMessageTemplate } from '../../../sagas/batchMessage/actions';
import {
  createBatchMessageTemplateRequest,
  createBatchMessageTemplateReset
} from '../../../sagas/sendbird/templates/create/actions';
import {
  updateBatchMessageTemplateRequest,
  updateBatchMessageTemplateReset
} from '../../../sagas/sendbird/templates/update/actions';

import './_batchMessageTemplatePopup.scss';
import BatchMessageVariables from '../BatchMessageComposer/components/Variables';
import PropTypes from 'prop-types';

const BatchMessageTemplatePopup = ({ variables }) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [hasFocused, setHasFocused] = useState(false);
  const { template: { open, id, edit, title, content } } = useSelector(s => s.batchMessage);
  const { errors_api } = useSelector(s => !edit ? s.createBatchMessageTemplate : s.updateBatchMessageTemplate);

  const allowedVars = useMemo(() => new Set(variables.map(v => v.key)), [variables]);

  const onClose = () => {
    setHasFocused(false);
    dispatch(closeBatchMessageTemplate());
  };

  const updateTemplate = (prop, value) => dispatch(setBatchMessageTemplate({ prop, value }));

  const setTitle = value => updateTemplate('title', value);

  const setMessage = value => updateTemplate('content', value);

  const applyVariable = value => {
    if (!allowedVars.has(value)) return;

    if (!hasFocused) {
      setMessage(`${content}${value}`);
      return;
    }

    const el = inputRef.current;
    if (!el) {
      setMessage(`${content}${value}`);
      return;
    }

    const start =
      typeof el.selectionStart === 'number' ? el.selectionStart : content.length;
    const end =
      typeof el.selectionEnd === 'number' ? el.selectionEnd : content.length;

    const nextMessage = content.slice(0, start) + value + content.slice(end);
    setMessage(nextMessage);

    requestAnimationFrame(() => {
      el.focus();
      const pos = start + value.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const saveTemplate = () => {
    if (!edit) {
      dispatch(createBatchMessageTemplateRequest({
        values: {
          title,
          content
        },
        onSuccess: () => {
          onClose();
        }
      }));
    } else {
      dispatch(updateBatchMessageTemplateRequest({
        id,
        values: {
          title,
          content
        },
        onSuccess: () => {
          onClose();
        }
      }));
    }
  };

  return (
    <Modal open={open} closeModal={onClose} className="batchMessageTemplatePopup-popup">
      <div className="batchMessageTemplatePopup-header">
        <p className="batchMessageTemplatePopup-title">{edit ? 'Edit' : 'Add'} template</p>
      </div>

      <div className="batchMessageTemplatePopup-container">
        <InputNew
          className="batchMessageTemplatePopup-name"
          placeholder="Title"
          value={title} onChange={e => setTitle(e.target.value)}
        />

        <BatchMessageVariables variables={variables} applyVariable={applyVariable} />

        <div className="batchMessageTemplatePopup-field">
          <MultilineInput
            ref={inputRef}
            value={content}
            onChange={e => setMessage(e.target.value)}
            onFocus={() => setHasFocused(true)}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '263px',
                boxSizing: 'border-box'
              },
              '& textarea': {
                height: '100% !important',
                overflow: 'auto !important',
                resize: 'none'
              }
            }}
          />
        </div>
      </div>

      <div className="batchMessageTemplatePopup-buttons">
        <ActionButton disabled={isEmpty(title)} title="Save" onClick={saveTemplate} />
      </div>
      <DownScreenMessage
        error
        duration={5000}
        message={errors_api && errors_api.non_field_errors || ''}
        open={Boolean(errors_api && errors_api.non_field_errors)}
        onClose={() => dispatch(!edit ? createBatchMessageTemplateReset() : updateBatchMessageTemplateReset())}
      />
    </Modal>
  );
};

BatchMessageTemplatePopup.propTypes = {
  variables: PropTypes.array
};

export default BatchMessageTemplatePopup;
